/**
 * Verbatim port of raven-core/src/encoding/matcher.ts.
 * Match-only — never decrypt. Detection here, decryption belongs to Raven.
 */

import type { EncodingRule } from './types.js';
import { analyzeText } from './analyzer.js';

export type RuleLike = Partial<EncodingRule> & {
  word_at?: Array<{ position: number; length: number }> | { position: number; length: number };
};

interface WordAtCheck {
  position: number;
  length: number;
}

function normalizeWordAt(raw: RuleLike['word_at']): WordAtCheck[] | null {
  if (raw === undefined) return null;
  if (Array.isArray(raw)) return raw;
  return [raw];
}

export function matchRule(text: string, rule: RuleLike): boolean {
  const analysis = analyzeText(text);

  if (rule.total_vowels !== undefined && analysis.totalVowels !== rule.total_vowels) return false;
  if (rule.e_count !== undefined && analysis.eCount !== rule.e_count) return false;
  if (rule.a_count !== undefined && analysis.aCount !== rule.a_count) return false;
  if (rule.i_count !== undefined && analysis.iCount !== rule.i_count) return false;
  if (rule.o_count !== undefined && analysis.oCount !== rule.o_count) return false;
  if (rule.u_count !== undefined && analysis.uCount !== rule.u_count) return false;

  if (rule.char_count !== undefined && analysis.charCount !== rule.char_count) return false;
  if (rule.word_count !== undefined && analysis.words.length !== rule.word_count) return false;
  if (rule.consonant_count !== undefined && analysis.consonantCount !== rule.consonant_count) return false;
  if (rule.longest_word_length !== undefined && analysis.longestWordLength !== rule.longest_word_length) return false;
  if (rule.shortest_word_length !== undefined && analysis.shortestWordLength !== rule.shortest_word_length) return false;
  if (rule.words_starting_with_vowel !== undefined && analysis.wordsStartingWithVowel !== rule.words_starting_with_vowel) return false;
  if (rule.words_ending_with_vowel !== undefined && analysis.wordsEndingWithVowel !== rule.words_ending_with_vowel) return false;
  if (rule.uppercase_count !== undefined && analysis.uppercaseCount !== rule.uppercase_count) return false;
  if (rule.digit_count !== undefined && analysis.digitCount !== rule.digit_count) return false;
  if (rule.punctuation_count !== undefined && analysis.punctuationCount !== rule.punctuation_count) return false;
  if (rule.emoji_count !== undefined && analysis.emojiCount !== rule.emoji_count) return false;
  if (rule.double_letter_count !== undefined && analysis.doubleLetterCount !== rule.double_letter_count) return false;

  const checks = normalizeWordAt(rule.word_at);
  if (checks !== null) {
    for (const c of checks) {
      const idx = c.position - 1;
      if (idx < 0 || idx >= analysis.words.length) return false;
      const w = analysis.words[idx];
      if (w === undefined || w.length !== c.length) return false;
    }
  }

  return true;
}
